// src/components/AssignTrailer.js

import React, { useState, useEffect, useMemo } from "react";
import axios from "../axiosConfig";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

function AssignTrailer() {
  const { t } = useTranslation("assign_trailer");

  const [trailers, setTrailers] = useState([]);
  const [selectedTrailerId, setSelectedTrailerId] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isAssigning, setIsAssigning] = useState(false);
  const [error, setError] = useState("");

  const { loadId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;

    const fetchTrailers = async () => {
      setIsLoading(true);
      setError("");

      try {
        const response = await axios.get("/trailers");
        if (!mounted) return;

        const list = Array.isArray(response.data) ? response.data : [];
        setTrailers(list);
      } catch (err) {
        console.error("[AssignTrailer] Error fetching trailers:", err);
        if (!mounted) return;
        setError(t("errors.fetchTrailers"));
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    fetchTrailers();

    return () => {
      mounted = false;
    };
  }, [t]);

  const handleAssignTrailer = async () => {
    if (!selectedTrailerId || isAssigning) return;

    setIsAssigning(true);
    setError("");

    try {
      await axios.post("/loads/assign-trailer", {
        loadId,
        trailerId: selectedTrailerId,
      });

      alert(t("alerts.assignedSuccess"));
      navigate("/dashboard");
    } catch (err) {
      console.error("[AssignTrailer] Error assigning trailer:", err);
      setError(t("errors.assignTrailer"));
    } finally {
      setIsAssigning(false);
    }
  };

  const trailerLabel = useMemo(() => {
    return (trailer) => {
      // fallback seguro para não partir o UI se algum campo vier null/undefined
      const trailerNumber = trailer?.trailerNumber ?? t("common.unknown");
      const type = trailer?.type ?? t("common.unknown");

      const height = trailer?.height ?? t("common.na");
      const width = trailer?.width ?? t("common.na");
      const length = trailer?.length ?? t("common.na");

      const axles = trailer?.numberOfAxles ?? t("common.na");
      const straps = trailer?.numberOfStraps ?? t("common.na");
      const ratchets = trailer?.numberOfRatchets ?? t("common.na");
      const edgeProtectors = trailer?.numberOfEdgeProtectors ?? t("common.na");
      const antiSlipMats = trailer?.numberOfAntiSlippingMattresses ?? t("common.na");
      const sidebars = trailer?.numberOfSidebars ?? t("common.na");
      const fireExtinguishers = trailer?.numberOfFireExtinguishers ?? t("common.na");
      const woodenBeams = trailer?.numberOfWoodenBeams ?? t("common.na");

      // string montada com labels traduzíveis
      return `${trailerNumber} - ${type}, ` +
        `${t("fields.height")}: ${height}, ` +
        `${t("fields.width")}: ${width}, ` +
        `${t("fields.length")}: ${length}, ` +
        `${t("fields.axles")}: ${axles}, ` +
        `${t("fields.straps")}: ${straps}, ` +
        `${t("fields.ratchets")}: ${ratchets}, ` +
        `${t("fields.edgeProtectors")}: ${edgeProtectors}, ` +
        `${t("fields.antiSlipMats")}: ${antiSlipMats}, ` +
        `${t("fields.sidebars")}: ${sidebars}, ` +
        `${t("fields.fireExtinguishers")}: ${fireExtinguishers}, ` +
        `${t("fields.woodenBeams")}: ${woodenBeams}`;
    };
  }, [t]);

  return (
    <div className="assign-trailer-container">
      <h1>{t("title", { loadId })}</h1>

      {error ? <div className="assign-trailer-error">{error}</div> : null}

      <div className="form-group">
        <label htmlFor="assign-trailer-select">{t("labels.chooseTrailer")}</label>

        <select
          id="assign-trailer-select"
          value={selectedTrailerId}
          onChange={(e) => setSelectedTrailerId(e.target.value)}
          disabled={isLoading || isAssigning}
        >
          <option value="">
            {isLoading ? t("states.loading") : t("placeholders.selectTrailer")}
          </option>

          {trailers.map((trailer) => (
            <option key={trailer.id} value={trailer.id}>
              {trailerLabel(trailer)}
            </option>
          ))}
        </select>
      </div>

      <button
        className="assign-btn"
        onClick={handleAssignTrailer}
        disabled={!selectedTrailerId || isLoading || isAssigning}
      >
        {isAssigning ? t("buttons.assigning") : t("buttons.assign")}
      </button>
    </div>
  );
}

export default AssignTrailer;
